import service from './service'

const state = {
  scope: null, // {type: 'global'|'contract-manager'|'pica-service', id: number}
  schedules: [],
  schedule: null,
  debugInfo: null
}

const getters = {
  scope: state => state.scope,
  schedules: state => state.schedules,
  schedule: state => state.schedule,
  debugInfo: state => state.debugInfo
}

const mutations = {
  setScope(state, scope) {
    state.scope = scope
  },
  setSchedules(state, schedules) {
    state.schedules = schedules || []
  },
  setSchedule(state, schedule) {
    state.schedule = schedule
  },
  deleteSchedule(state, scheduleId) {
    state.schedules = state.schedules.filter(s => s.id !== scheduleId)
  },
  setDebugInfo(state, debugInfo) {
    state.debugInfo = debugInfo
  }
}

const actions = {
  /**
   * Set the current scope and fetch schedules
   * @param {Object} context
   * @param {Object} scope - {type: 'global'|'contract-manager'|'pica-service', id: number}
   */
  async setScope({ commit, dispatch }, scope) {
    commit('setScope', scope)
    await dispatch('fetchEmailSchedules')
  },

  /**
   * Fetch all email schedules for current scope
   * @param {Object} context
   */
  async fetchEmailSchedules({ commit, state }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const response = await service.fetchEmailSchedules(state.scope)
    // ApiClient unwraps .data automatically, backend returns {count, results}
    const schedules = response.results || []
    commit('setSchedules', schedules)
  },

  /**
   * Fetch a single email schedule
   * @param {Object} context
   * @param {string} scheduleId
   */
  async fetchEmailSchedule({ commit, state }, scheduleId) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const data = await service.fetchEmailSchedule(state.scope, scheduleId)
    commit('setSchedule', data)
  },

  /**
   * Create a new email schedule
   * @param {Object} context
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async createEmailSchedule({ commit, state }, data) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const result = await service.createEmailSchedule(state.scope, data)
    commit('setSchedule', result)
    return result
  },

  /**
   * Update an existing email schedule
   * @param {Object} context
   * @param {Object} payload - {scheduleId: string, data: Object}
   * @returns {Promise<Object>}
   */
  async updateEmailSchedule({ commit, state }, { scheduleId, data }) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const result = await service.updateEmailSchedule(state.scope, scheduleId, data)
    commit('setSchedule', result)
    return result
  },

  /**
   * Delete an email schedule
   * @param {Object} context
   * @param {string} scheduleId
   */
  async deleteEmailSchedule({ commit, state }, scheduleId) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    await service.deleteEmailSchedule(state.scope, scheduleId)
    commit('deleteSchedule', scheduleId)
  },

  /**
   * Force restart an email schedule
   * @param {Object} context
   * @param {string} scheduleId
   * @returns {Promise<Object>}
   */
  async forceRestartEmailSchedule({ state }, scheduleId) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    return await service.forceRestart(state.scope, scheduleId)
  },

  /**
   * Send now - trigger immediate execution of an email schedule
   * @param {Object} context
   * @param {string} scheduleId
   * @returns {Promise<Object>}
   */
  async sendNowEmailSchedule({ state }, scheduleId) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    return await service.sendNow(state.scope, scheduleId)
  },

  /**
   * Fetch debug info for an email schedule
   * @param {Object} context
   * @param {string} scheduleId
   * @returns {Promise<Object>}
   */
  async fetchDebugInfo({ commit, state }, scheduleId) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    const data = await service.fetchDebugInfo(state.scope, scheduleId)
    commit('setDebugInfo', data)
    return data
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
